import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Loader2, Plus, Pencil, Trash2, X, ImagePlus } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EMPTY = {
  title: "",
  city: "",
  room_type: "private",
  price: "",
  includedText: "",
  status: "available",
  photo: null,
};

const downscaleImage = (file) =>
  new Promise((resolve) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const img = new Image();
      img.onload = () => {
        const max = 1200;
        const scale = Math.min(1, max / Math.max(img.width, img.height));
        const canvas = document.createElement("canvas");
        canvas.width = Math.round(img.width * scale);
        canvas.height = Math.round(img.height * scale);
        canvas.getContext("2d").drawImage(img, 0, 0, canvas.width, canvas.height);
        resolve(canvas.toDataURL("image/jpeg", 0.82));
      };
      img.src = e.target.result;
    };
    reader.readAsDataURL(file);
  });

export default function Admin() {
  const [password, setPassword] = useState(sessionStorage.getItem("shonta_admin") || "");
  const [authed, setAuthed] = useState(false);
  const [rooms, setRooms] = useState([]);
  const [form, setForm] = useState(EMPTY);
  const [editingId, setEditingId] = useState(null);
  const [busy, setBusy] = useState(false);
  const [loginInput, setLoginInput] = useState("");

  const headers = { "X-Admin-Password": password };

  const loadRooms = async () => {
    const res = await axios.get(`${API}/rooms`);
    setRooms(res.data);
  };

  useEffect(() => {
    if (password) {
      axios
        .post(`${API}/admin/login`, { password })
        .then(() => {
          setAuthed(true);
          loadRooms();
        })
        .catch(() => {
          sessionStorage.removeItem("shonta_admin");
          setPassword("");
        });
    }
  }, [password]);

  const handleLogin = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/admin/login`, { password: loginInput });
      sessionStorage.setItem("shonta_admin", loginInput);
      setPassword(loginInput);
    } catch {
      toast.error("Wrong password.");
    }
  };

  const handlePhoto = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      toast.error("Please choose a photo under 5MB.");
      return;
    }
    const dataUrl = await downscaleImage(file);
    setForm((f) => ({ ...f, photo: dataUrl }));
  };

  const resetForm = () => {
    setForm(EMPTY);
    setEditingId(null);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setBusy(true);
    const payload = {
      title: form.title,
      city: form.city,
      room_type: form.room_type,
      price: form.price,
      included: form.includedText.split("\n").map((s) => s.trim()).filter(Boolean),
      status: form.status,
      photo: form.photo,
    };
    try {
      if (editingId) {
        await axios.put(`${API}/admin/rooms/${editingId}`, payload, { headers });
        toast.success("Room updated.");
      } else {
        await axios.post(`${API}/admin/rooms`, payload, { headers });
        toast.success("Room added.");
      }
      resetForm();
      await loadRooms();
    } catch {
      toast.error("Could not save the room. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  const handleEdit = (room) => {
    setEditingId(room.id);
    setForm({
      title: room.title,
      city: room.city,
      room_type: room.room_type,
      price: room.price,
      includedText: (room.included || []).join("\n"),
      status: room.status,
      photo: room.photo || null,
    });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleDelete = async (room) => {
    if (!window.confirm(`Remove "${room.title}" from the website?`)) return;
    try {
      await axios.delete(`${API}/admin/rooms/${room.id}`, { headers });
      toast.success("Room removed.");
      await loadRooms();
    } catch {
      toast.error("Could not remove the room.");
    }
  };

  if (!authed) {
    return (
      <div className="min-h-screen bg-[#F7F7F9] flex items-center justify-center px-5" data-testid="admin-login">
        <form onSubmit={handleLogin} className="bg-white border border-[#EBEBEB] rounded-2xl p-8 w-full max-w-sm shadow-[0_12px_32px_rgba(0,0,0,0.08)]">
          <img src="/logo-512.png" alt="Shonta Solutions LLC" className="h-12 w-auto mx-auto" />
          <h1 className="mt-5 text-xl font-bold text-center text-[#222]">Room Listings Admin</h1>
          <p className="mt-1 text-sm text-center text-[#717171]">Enter your admin password to manage rooms.</p>
          <Input
            type="password"
            data-testid="admin-password-input"
            value={loginInput}
            onChange={(e) => setLoginInput(e.target.value)}
            placeholder="Admin password"
            className="mt-6 rounded-xl h-12"
            required
          />
          <button
            type="submit"
            data-testid="admin-login-button"
            className="mt-4 w-full bg-[#FF5A5F] hover:bg-[#E0484D] text-white font-bold px-6 py-3 rounded-full transition-colors"
          >
            Sign In
          </button>
          <a href="/" className="mt-4 block text-center text-sm font-semibold text-[#717171] hover:text-[#FF5A5F]">
            Back to website
          </a>
        </form>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F7F7F9] py-10 px-5" data-testid="admin-dashboard">
      <div className="max-w-4xl mx-auto">
        <div className="flex items-center justify-between">
          <h1 className="text-2xl font-extrabold text-[#222]">Room Listings</h1>
          <a href="/" className="text-sm font-semibold text-[#717171] hover:text-[#FF5A5F]">View website</a>
        </div>

        <form onSubmit={handleSave} className="mt-6 bg-white border border-[#EBEBEB] rounded-2xl p-6 sm:p-8 space-y-5" data-testid="admin-room-form">
          <h2 className="text-lg font-bold text-[#222]">{editingId ? "Edit Room" : "Add a Room"}</h2>
          <div className="grid sm:grid-cols-2 gap-5">
            <div className="space-y-2">
              <Label>Room Name</Label>
              <Input data-testid="admin-title-input" required value={form.title} onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))} placeholder="Sunny Private Bedroom" className="rounded-xl h-11" />
            </div>
            <div className="space-y-2">
              <Label>City / Neighborhood</Label>
              <Input data-testid="admin-city-input" required value={form.city} onChange={(e) => setForm((f) => ({ ...f, city: e.target.value }))} placeholder="Orlando — Pine Hills" className="rounded-xl h-11" />
            </div>
          </div>
          <div className="grid sm:grid-cols-3 gap-5">
            <div className="space-y-2">
              <Label>Room Type</Label>
              <Select value={form.room_type} onValueChange={(v) => setForm((f) => ({ ...f, room_type: v }))}>
                <SelectTrigger data-testid="admin-type-select" className="rounded-xl h-11">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="private">Private bedroom</SelectItem>
                  <SelectItem value="shared">Bed in a shared bedroom</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Monthly Price</Label>
              <Input data-testid="admin-price-input" required value={form.price} onChange={(e) => setForm((f) => ({ ...f, price: e.target.value }))} placeholder="$750" className="rounded-xl h-11" />
            </div>
            <div className="space-y-2">
              <Label>Availability</Label>
              <Select value={form.status} onValueChange={(v) => setForm((f) => ({ ...f, status: v }))}>
                <SelectTrigger data-testid="admin-status-select" className="rounded-xl h-11">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="available">Available Now</SelectItem>
                  <SelectItem value="coming_soon">Coming Soon</SelectItem>
                  <SelectItem value="full">Currently Full</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="space-y-2">
            <Label>What's Included (one per line)</Label>
            <Textarea data-testid="admin-included-input" value={form.includedText} onChange={(e) => setForm((f) => ({ ...f, includedText: e.target.value }))} placeholder={"Utilities included\nWi-Fi\nFurnished"} className="rounded-xl min-h-[90px]" />
          </div>
          <div className="space-y-2">
            <Label>Room Photo</Label>
            <label className="flex items-center gap-3 bg-[#F7F7F9] border border-dashed border-[#EBEBEB] rounded-xl px-4 py-4 cursor-pointer hover:border-[#FF5A5F] transition-colors" data-testid="admin-photo-upload">
              <ImagePlus className="w-5 h-5 text-[#FF5A5F]" />
              <span className="text-sm font-semibold text-[#555]">{form.photo ? "Photo selected — click to change" : "Choose a photo (max 5MB)"}</span>
              <input type="file" accept="image/*" className="hidden" onChange={handlePhoto} />
            </label>
            {form.photo && <img src={form.photo} alt="Room preview" className="h-32 rounded-xl object-cover" data-testid="admin-photo-preview" />}
          </div>
          <div className="flex gap-3">
            <button type="submit" data-testid="admin-save-button" disabled={busy} className="inline-flex items-center gap-2 bg-[#FF5A5F] hover:bg-[#E0484D] disabled:opacity-60 text-white font-bold px-6 py-3 rounded-full transition-colors">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
              {editingId ? "Save Changes" : "Add Room"}
            </button>
            {editingId && (
              <button type="button" onClick={resetForm} data-testid="admin-cancel-button" className="inline-flex items-center gap-2 bg-white border border-[#EBEBEB] text-[#222] font-bold px-6 py-3 rounded-full">
                <X className="w-4 h-4" /> Cancel
              </button>
            )}
          </div>
        </form>

        <div className="mt-8 space-y-4" data-testid="admin-room-list">
          {rooms.length === 0 && (
            <p className="text-sm text-[#717171] bg-white border border-[#EBEBEB] rounded-2xl p-6">No rooms posted yet. Add your first room above.</p>
          )}
          {rooms.map((room) => (
            <div key={room.id} className="flex items-center gap-4 bg-white border border-[#EBEBEB] rounded-2xl p-4" data-testid={`admin-room-row-${room.id}`}>
              {room.photo && <img src={room.photo} alt="" className="w-16 h-16 rounded-xl object-cover shrink-0" />}
              <div className="flex-1 min-w-0">
                <p className="font-bold text-[#222] truncate">{room.title}</p>
                <p className="text-sm text-[#717171]">
                  {room.city} · {room.room_type === "private" ? "Private" : "Shared"} · {room.price}/mo · {room.status.replace("_", " ")}
                </p>
              </div>
              <button onClick={() => handleEdit(room)} data-testid={`admin-edit-${room.id}`} className="p-2.5 rounded-xl bg-[#F7F7F9] hover:bg-[#EDE9E1]/60 transition-colors" aria-label="Edit room">
                <Pencil className="w-4 h-4 text-[#222]" />
              </button>
              <button onClick={() => handleDelete(room)} data-testid={`admin-delete-${room.id}`} className="p-2.5 rounded-xl bg-[#F7F7F9] hover:bg-red-50 transition-colors" aria-label="Delete room">
                <Trash2 className="w-4 h-4 text-[#E0484D]" />
              </button>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
